import { useEffect, useCallback } from 'react';
import {
  SettingsChangeEvent,
  useSubscribeForSettingsChange,
  fireSettingsChange,
} from '../events/SettingsChangeEvent';
import { GlobalSettingsKeys } from '../GlobalSettingsKeys';
import { isAppSwitcherEnabled } from '@sas/nova-core/app-switcher/ApplicationSwitcherStatus';
import { PREFERENCE_CHANGED } from '@sas/nova-core/app-switcher/events/ApplicationSwitcherObservable';

// Actually, it make sense to only pass the changes to global settings between apps
// but making it compatible with UI5
export function useAppSwitcherPreferenceItemsMessaging(currentAppId: string | undefined) {
  // the structure being used for AppSwitcher preference change
  // message data is compatible with What we have
  // for OpenUI5 App to help in a deployment consisting
  // of NOVA as well as OpenUI5 apps.
  const changeListener = useCallback(
    (_: string, data?: { data?: SettingsChangeEvent }) => {
      const event = data?.data;
      if (event && event.appId === currentAppId) {
        const globalSettingsIds: string[] = Object.keys(GlobalSettingsKeys).map(
          (key) => GlobalSettingsKeys[key as keyof typeof GlobalSettingsKeys]
        );

        const change = event.data
          .filter((item) => globalSettingsIds.indexOf(item.key) >= 0)
          .map((item) => ({
            id: item.key,
            newValue: item.value,
            oldValue: item.oldValue,
          }));

        // AppSwitcher broadcasting
        if (isAppSwitcherEnabled()) {
          import('../../NovaApplicationSwitcher').then((AppSwitcherModule) =>
            AppSwitcherModule.applicationSwitcher.broadcastMessage(PREFERENCE_CHANGED, {
              broadcaster: { appId: currentAppId },
              settings: change,
            })
          );
        }
      }
    },
    [currentAppId]
  );

  // settings messaging
  useSubscribeForSettingsChange(changeListener);

  // AppSwitcher subscription
  const appSwitcherPrefMessageSubscription = useCallback(
    (_: any, data: any) => {
      if (data.broadcaster.appId !== currentAppId) {
        fireSettingsChange(
          data.settings.map((item: any) => ({
            key: item.id,
            value: item.newValue?.toString(),
            oldValue: item.oldValue?.toString(),
          })),
          data.broadcaster.appId
        );
      }
    },
    [currentAppId]
  );

  useEffect(() => {
    if (isAppSwitcherEnabled()) {
      import('../../NovaApplicationSwitcher').then((AppSwitcherModule) =>
        AppSwitcherModule.applicationSwitcher.subscribe(
          PREFERENCE_CHANGED,
          appSwitcherPrefMessageSubscription
        )
      );
    }
    return () => {
      if (isAppSwitcherEnabled()) {
        import('../../NovaApplicationSwitcher').then((AppSwitcherModule) =>
          AppSwitcherModule.applicationSwitcher.unsubscribe(
            PREFERENCE_CHANGED,
            appSwitcherPrefMessageSubscription
          )
        );
      }
    };
  }, [appSwitcherPrefMessageSubscription]);
}
